import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import moment from "moment";
import {PlayerAPI} from "@/api/player";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {rosterMeasurementAverage} from "@/utils/rosterCondition";

export default function RosterCondition({team, fromDate, toDate}) {
	const [data, setData] = useState({tqr: [], rpe: [], medical: []});
	const [loading, setLoading] = useState(true);
	const [errors, setErrors] = useState({});
	useEffect(() => {
		let active = true;
		setLoading(true);
		setErrors({});
		setData({tqr: [], rpe: [], medical: []});
		const params = {
			rosterId: team.id,
			seasonId: team.season?.id,
			fromDate,
			toDate,
		};
		Promise.allSettled([
			PlayerAPI.listTQR(params),
			PlayerAPI.listRPE({...params, includePreparation: false}),
			MedicalAPI.recordsList({rosterId: team.id, seasonId: team.season?.id}),
		])
			.then((results) => {
				if (!active) return;
				const next = {},
					failed = {};
				["tqr", "rpe", "medical"].forEach((key, index) => {
					next[key] =
						results[index].status === "fulfilled"
							? results[index].value || []
							: [];
					failed[key] = results[index].status === "rejected";
				});
				setData(next);
				setErrors(failed);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [team.id, team.season?.id, fromDate, toDate]);
	const playerIds = useMemo(
		() =>
			new Set(
				(team.players || [])
					.map((entry) => entry.playerId || entry.player?.id)
					.filter(Boolean),
			),
		[team.players],
	);
	const now = moment();
	const tqr = rosterMeasurementAverage(
		data.tqr,
		playerIds,
		"tqrValue",
		"tqrDate",
		fromDate,
		toDate,
		now,
	);
	const rpe = rosterMeasurementAverage(
		data.rpe,
		playerIds,
		"rpe",
		"rpeDate",
		fromDate,
		toDate,
		now,
	);
	const injuries = data.medical
		.filter((record) => playerIds.has(record.playerId || record.playerData?.id))
		.flatMap((record) =>
			(record.injuriesIllness || [])
				.filter((injury) => isInjuryActiveAt(injury, toDate))
				.map((injury) => ({
					...injury,
					recordId: record.id,
					playerId: record.playerId || record.playerData?.id,
					name: [record.playerData?.firstName, record.playerData?.lastName]
						.filter(Boolean)
						.join(" "),
				})),
		)
		.sort((a, b) => moment(b.startDate).diff(moment(a.startDate)));
	const injuredPlayers = new Set(injuries.map((injury) => injury.playerId));
	const newPlayers = new Set(
		injuries
			.filter((injury) =>
				moment(injury.startDate).isSameOrAfter(fromDate, "day"),
			)
			.map((injury) => injury.playerId),
	);
	const value = (metric) =>
		metric.value == null
			? "—"
			: metric.value.toLocaleString("it-IT", {
					maximumFractionDigits: 1,
					minimumFractionDigits: 1,
				});
	return (
		<section className="roster-panorama__panel roster-panorama__condition">
			<header>
				<h3>Recupero e disponibilità</h3>
				<span>
					{moment(fromDate).format("DD/MM")}–{moment(toDate).format("DD/MM")}
				</span>
			</header>
			{loading ? (
				<p role="status">Caricamento indicatori…</p>
			) : (
				<>
					<div className="roster-panorama__condition-kpis">
						<div>
							<span>Recupero · TQR</span>
							<strong>
								{errors.tqr ? "—" : value(tqr)}
								{tqr.value != null && !errors.tqr && <small>/20</small>}
							</strong>
							<span>
								{errors.tqr
									? "Non disponibile"
									: `${tqr.players}/${playerIds.size} giocatori`}
							</span>
						</div>
						<div>
							<span>Sforzo · RPE</span>
							<strong>
								{errors.rpe ? "—" : value(rpe)}
								{rpe.value != null && !errors.rpe && <small>/10</small>}
							</strong>
							<span>
								{errors.rpe
									? "Non disponibile"
									: `${rpe.players}/${playerIds.size} giocatori`}
							</span>
						</div>
						<div
							className={
								!errors.medical && injuredPlayers.size
									? "roster-panorama__condition-injured"
									: ""
							}
						>
							<span>Infortunati oggi</span>
							<strong>
								{errors.medical ? "—" : injuredPlayers.size}
								<small>/{playerIds.size}</small>
							</strong>
							<span>
								{errors.medical
									? "Non disponibile"
									: `${newPlayers.size} nuovi attivi nei 7 gg`}
							</span>
						</div>
					</div>
					<details className="roster-panorama__reading">
						<summary>
							Dettagli e copertura
							{injuries.length ? ` · ${injuredPlayers.size} infortunati` : ""}
						</summary>
						<p>
							Medie degli ultimi 7 giorni, prima per giocatore e poi per rosa,
							come in dashboard. TQR: {tqr.measurements} rilevazioni; RPE:{" "}
							{rpe.measurements}. Dati mancanti esclusi, non zero. Il TQR
							descrive il recupero percepito e l'RPE lo sforzo: non sono un voto
							complessivo di forma.
						</p>
						{errors.medical ? (
							<p role="alert">
								Dati medici non disponibili: il conteggio degli infortunati non
								è noto.
							</p>
						) : !injuries.length ? (
							<p>
								Nessun infortunio attivo registrato nelle cartelle disponibili.
							</p>
						) : (
							<div className="roster-panorama__injury-list">
								{injuries.map((injury) => (
									<Link
										key={injury.id}
										to={`/medical/records/${injury.recordId}`}
									>
										<strong>{injury.name || "Giocatore"}</strong>
										<span>
											{injury.diagnosis || "Infortunio"} · dal{" "}
											{moment(injury.startDate).format("DD/MM")}
											{injury.endDate
												? ` · fine indicata ${moment(injury.endDate).format("DD/MM")}`
												: ""}
										</span>
									</Link>
								))}
							</div>
						)}
						<p>
							Infortuni attivi oggi secondo la dimissione sportiva; non solo
							quelli iniziati nel periodo. La fine indicata non certifica il
							rientro.
						</p>
					</details>
				</>
			)}
		</section>
	);
}
